import { Logo } from './Logo';

type Screen = 'domains' | 'detail' | 'detail-audits' | 'detail-suggestions' | 'detail-content' | 'detail-activity' | 'detail-outreach' | 'add' | 'afi-overview' | 'afi-review' | 'afi-activity' | 'afi-brand-voice' | 'afi-settings' | 'agency-overview';

interface Props {
  tier: string;
  email: string;
  activeDomainId: string | null;
  screen: Screen;
  onNavigate: (screen: Screen) => void;
  onLogout: () => void;
  onUpgrade?: () => void;
}

type Tier = 'free' | 'tier_1' | 'tier_2' | 'tier_3';
const TIER_RANK: Record<string, number> = { free: 0, tier_1: 1, tier_2: 2, tier_3: 3 };
const TIER_LABEL: Record<string, string> = { free: 'Free', tier_1: 'Starter', tier_2: 'Pro', tier_3: 'Scale' };

function meetsMinTier(userTier: string, min: Tier): boolean {
  return (TIER_RANK[userTier] ?? 0) >= (TIER_RANK[min] ?? 1);
}

interface NavItem {
  id: Screen;
  label: string;
  icon: string;
  minTier: Tier;
  requiresDomain?: boolean;
  external?: string;
  subItem?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'domains',              label: 'Domains',        icon: '⬡', minTier: 'free' },
  { id: 'detail',               label: 'Overview',       icon: '◎', minTier: 'free',   requiresDomain: true },
  { id: 'detail-audits',        label: 'Audits',         icon: '↗', minTier: 'free',   requiresDomain: true },
  { id: 'add',                  label: 'Add domain',     icon: '+', minTier: 'tier_1' },
  { id: 'detail-suggestions',   label: 'Suggestions',    icon: '✦', minTier: 'tier_1', requiresDomain: true },
  { id: 'detail-content',       label: 'Content',        icon: '✎', minTier: 'tier_1', requiresDomain: true },
  { id: 'detail-activity',      label: 'Activity',       icon: '⟳', minTier: 'tier_1', requiresDomain: true },
  { id: 'detail-outreach',      label: 'Outreach',       icon: '✉', minTier: 'tier_2', requiresDomain: true },
  { id: 'agency-overview',      label: 'Agency overview', icon: '◫', minTier: 'tier_3' },
];

const AFI_NAV_ITEMS: NavItem[] = [
  { id: 'afi-overview',    label: 'AFI overview',   icon: '◈', minTier: 'tier_1', requiresDomain: true },
  { id: 'afi-review',      label: 'Review queue',   icon: '⬚', minTier: 'tier_1', requiresDomain: true },
  { id: 'afi-activity',    label: 'Activity',       icon: '≡', minTier: 'tier_1', requiresDomain: true },
  { id: 'afi-brand-voice', label: 'Brand voice',    icon: '⬦', minTier: 'tier_2', requiresDomain: true },
  { id: 'afi-settings',    label: 'AFI settings',   icon: '⚙', minTier: 'tier_1', requiresDomain: true },
];

export function Sidebar({ tier, email, activeDomainId, screen, onNavigate, onLogout, onUpgrade }: Props) {
  return (
    <aside className="w-56 min-h-screen bg-brand-ink flex flex-col shrink-0">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-white/10">
        <Logo size="sm" />
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV_ITEMS.map((item, i) => {
          const unlocked = meetsMinTier(tier, item.minTier);
          const hasDomain = !!activeDomainId;
          const reportBlocked = false;
          const disabled = !item.external && (!unlocked || (item.requiresDomain && !hasDomain) || reportBlocked);
          const isActive = !item.external && screen === item.id && !(item.requiresDomain && !hasDomain);

          const className = `w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors
            ${item.subItem ? 'pl-8' : ''}
            ${isActive
              ? 'bg-white/10 text-white font-medium'
              : disabled
              ? 'text-white/20 cursor-not-allowed'
              : 'text-white/60 hover:bg-white/5 hover:text-white'
            }`;

          const inner = (
            <>
              <span className="text-base w-4 text-center">{item.icon}</span>
              <span className="flex-1 text-left">{item.label}</span>
              {item.external && <span className="text-[10px] text-white/30">↗</span>}
              {!item.external && !unlocked && (
                <span className="text-[10px] bg-white/10 text-white/40 px-1.5 py-0.5 rounded">
                  {(TIER_LABEL[item.minTier] ?? item.minTier)}+
                </span>
              )}
            </>
          );

          if (item.external) {
            return (
              <a
                key={`${item.id}-${i}`}
                href={item.external}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
              >
                {inner}
              </a>
            );
          }

          return (
            <button
              key={`${item.id}-${i}`}
              onClick={() => { if (!disabled) onNavigate(item.id); }}
              disabled={disabled}
              title={!unlocked ? `Requires ${item.minTier} plan` : item.requiresDomain && !hasDomain ? 'Select a domain first' : undefined}
              className={className}
            >
              {inner}
            </button>
          );
        })}

        {/* AFI section divider */}
        <div className="pt-3 pb-1">
          <div className="border-t border-white/10" />
          <p className="text-[10px] text-white/20 font-semibold uppercase tracking-widest px-3 pt-2">Auto-fix</p>
        </div>

        {AFI_NAV_ITEMS.map((item, i) => {
          const unlocked = meetsMinTier(tier, item.minTier);
          const hasDomain = !!activeDomainId;
          const disabled = !unlocked || (!!item.requiresDomain && !hasDomain);
          const isActive = screen === item.id;

          const className = `w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors
            ${isActive
              ? 'bg-white/10 text-white font-medium'
              : disabled
              ? 'text-white/20 cursor-not-allowed'
              : 'text-white/60 hover:bg-white/5 hover:text-white'
            }`;

          return (
            <button
              key={`afi-${item.id}-${i}`}
              onClick={() => { if (!disabled) onNavigate(item.id); }}
              disabled={disabled}
              title={
                !unlocked
                  ? `Requires ${item.minTier} plan`
                  : item.requiresDomain && !hasDomain
                  ? 'Select a domain first'
                  : undefined
              }
              className={className}
            >
              <span className="text-base w-4 text-center">{item.icon}</span>
              <span className="flex-1 text-left">{item.label}</span>
              {!unlocked && (
                <span className="text-[10px] bg-white/10 text-white/40 px-1.5 py-0.5 rounded capitalize">
                  {item.minTier}+
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User + upgrade hint */}
      <div className="px-4 py-4 border-t border-white/10 space-y-2">
        {tier === 'free' && (
          <button
            onClick={onUpgrade}
            className="w-full text-left bg-brand-green/10 border border-brand-green/30 rounded-lg px-3 py-2 hover:bg-brand-green/20 hover:border-brand-green/50 transition-colors cursor-pointer"
          >
            <p className="text-[11px] text-brand-green font-medium">Free plan</p>
            <p className="text-[10px] text-white/40 mt-0.5">Upgrade to unlock suggestions &amp; content editing →</p>
          </button>
        )}
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-xs text-white/60 truncate">{email}</p>
            <p className="text-[10px] text-white/30">{TIER_LABEL[tier] ?? tier}</p>
          </div>
          <button
            onClick={onLogout}
            className="text-xs text-white/30 hover:text-white/60 transition-colors ml-2 shrink-0"
          >
            Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}
